/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : RaiseSoftwareTickets.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, DialogActions, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import { componentStyle } from "../../../config/appTheme";
import RaiseTicketTemplate from "./RaiseTicketTemplate";
import SelectTemplate from "./SelectDialog/SelectTemplate";
import DialogIndex from "./SelectDialog/DialogIndex";
import KeyValueDisplayCard from "./SelectDialog/KeyValueDisplayCard";
import CustomCheckBoxWithoutFormControl from "../../../components/customComponentsV2/common/checkBox/CustomCheckBoxWithoutFormControl";
import RaiseTicketRightTemplate from "./RaiseTicketRightTemplate";
import { CAPABILITY_CODE } from "../../../config/appConstants";
import EESWMSavedLinkageCard from "./EESWMSavedLinkageCard";
import softwareIntersectionAPI from "../../../redux/actions/softwareIntersectionActions";
import { HandleApiActions } from "../../../redux/actions/actionHandler";
import CustomToolTip from "../../../components/infoButton/CustomToolTip";

import { ENTITY } from "../../../components/customComponentsV2/SmartMatchingDialogContainer/constants/entityTypes";
import AISuggestionDialogContainer from "../../../components/customComponentsV2/SmartMatchingDialogContainer/AiSuggestionDialogContainer";
import { labCar, legalRequirements, userManagementApis } from "../../../redux/actions";
import testCasesActions from "../../../redux/actions/testCasesActions";
import TestCaseCampaignLinkDialog from "./TestCaseCampaignLinkDialog";
import TestCaseComponentLinkDialog from "./TestCaseComponentLinkDialog";
import typeApprovalAPI from "../../../redux/actions/typeApprovalAPI";
import SelectDialogActionButtons from "./SelectDialog/SelectDialogActionButtons";
import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";

const removeDuplicates = (arr = []) => {
  if (!Array.isArray(arr)) return [];
  const seen = new Map();
  for (const user of arr) {
    if (!user?.user_id) continue;
    if (seen.has(user.user_id)) {
      const existing = seen.get(user.user_id);
      if (
        user.component_name &&
        existing.component_name &&
        !existing.component_name.includes(user.component_name)
      ) {
        existing.component_name += ", " + user.component_name;
      }
    } else {
      seen.set(user.user_id, { ...user });
    }
  }
  return Array.from(seen.values());
};

const insertCapabilityCodeToUserList = (userDetails = [], allUsersList = []) => {
  if (!Array.isArray(userDetails)) return [];
  const userCapMap = new Map();
  for (const au of allUsersList) {
    if (au?.user_id && au?.role_details?.capability_code) {
      userCapMap.set(au.user_id, au.role_details.capability_code);
    }
  }
  return userDetails.map((u) => {
    const capCode = userCapMap.get(u?.user_id);
    return capCode ? { ...u, capability_code: capCode } : u;
  });
};

const gatherFromAll = (data = [], field = "", allUsersList = []) => {
  return (data || []).flatMap((swm) =>
    swm?.[field]
      ? insertCapabilityCodeToUserList(swm[field], allUsersList).map((u) => ({
          ...u,
          component_name: swm.name,
        }))
      : [],
  );
};

const extractUsersFromSWMData = (swmData = [], allUsersList = []) => {
  const data = Array.isArray(swmData) ? swmData : [swmData];
  const ownerDetails = gatherFromAll(data, "owner_details", allUsersList);
  const coOwnerDetails = [
    ...gatherFromAll(data, "co_owners", allUsersList),
    ...gatherFromAll(data, "co_owner_details", allUsersList),
  ];
  const editorDetails = gatherFromAll(data, "edit_user_details", allUsersList);
  const viewersDetails = gatherFromAll(data, "view_user_details", allUsersList);

  const duplicateRemovedComponentRequestor = removeDuplicates([
    ...ownerDetails,
    ...coOwnerDetails,
  ]);
  const requestors = duplicateRemovedComponentRequestor.filter(
    (ele) => ele?.capability_code !== CAPABILITY_CODE.supplier,
  );

  const duplicateRemovedComponentSupplierDetails = removeDuplicates([
    ...coOwnerDetails,
    ...editorDetails,
    ...viewersDetails,
  ]);
  const suppliers = duplicateRemovedComponentSupplierDetails.filter(
    (ele) => ele?.capability_code === CAPABILITY_CODE.supplier,
  );

  return { requestors, suppliers };
};

const hasSameSWMs = (arr1, arr2) => {
  if (!arr1 || !arr2 || arr1.length !== arr2.length) return false;
  const set1 = new Set(arr1.map(Number));
  return arr2.every((id) => set1.has(Number(id)));
};

const replaceTestCaseIds = (testCases, idReplacementMap) => {
  if (!Array.isArray(testCases)) return testCases;
  return testCases.map((tc) => {
    const newId = idReplacementMap.get(String(tc?.test_case_id));
    return newId ? { ...tc, test_case_id: newId } : tc;
  });
};

const replaceIdInArray = (ids, idReplacementMap) => {
  if (!Array.isArray(ids)) return ids;
  return ids.map((id) => idReplacementMap.get(String(id)) ?? id);
};

const updateLegalItemWithNewIds = (legalItem, idReplacementMap) => {
  const updatedLegal = { ...legalItem };
  if (Array.isArray(updatedLegal.test_case_ids)) {
    updatedLegal.test_case_ids = replaceIdInArray(
      updatedLegal.test_case_ids,
      idReplacementMap,
    );
  }
  if (Array.isArray(updatedLegal.test_cases)) {
    updatedLegal.test_cases = replaceTestCaseIds(
      updatedLegal.test_cases,
      idReplacementMap,
    );
  }
  return updatedLegal;
};

const updateLinkageWithNewIds = (linkage, idReplacementMap) => {
  const updatedLinkage = { ...linkage };

  if (Array.isArray(updatedLinkage.test_case_ids)) {
    updatedLinkage.test_case_ids = replaceIdInArray(
      updatedLinkage.test_case_ids,
      idReplacementMap,
    );
  }

  if (Array.isArray(updatedLinkage.test_cases)) {
    updatedLinkage.test_cases = replaceTestCaseIds(
      updatedLinkage.test_cases,
      idReplacementMap,
    );
  }

  if (Array.isArray(updatedLinkage.legal)) {
    updatedLinkage.legal = updatedLinkage.legal.map((legalItem) =>
      updateLegalItemWithNewIds(legalItem, idReplacementMap),
    );
  }

  return updatedLinkage;
};

function RaiseSoftwareTickets({
  savedLinkages,
  setSavedLinkages,
  entityType,
}) {
  const dispatch = useDispatch();
  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariantDetails = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );
  const [zoneList, setZoneList] = useState([]);
  const [isZonalProject, setIsZonalProject] = useState(false);
  const [openDuplicateAlert, setOpenDuplicateAlert] = useState(false);
  const [duplicateAlertMessage, setDuplicateAlertMessage] = useState("");

  const [selectedSWMMain, setSelectedSWMMain] = useState([]);
  const [openEESelect, setOpenEESelect] = useState(false);
  const [formResetKey, setFormResetKey] = useState(0);

  const [selectedLegalMain, setSelectedLegalMain] = useState([]);
  const [selectedLegalForDisplay, setSelectedLegalForDisplay] = useState([]);
  const [disabledLegalRequirement, setDisabledLegalRequirement] = useState([]);
  const [openLegalSelect, setOpenLegalSelect] = useState(false);
  const [disabledSWMComponents, setDisabledSWMComponents] = useState([]);

  const [openTestCaseSelect, setOpenTestCaseSelect] = useState(false);
  const [selectedTestCaseForDisplay, setSelectedTestCaseForDisplay] =
    useState(null);
  const [selectedTestCaseMain, setSelectedTestCaseMain] = useState(null);

  const [selectedComponentRequestor, setSelectedComponentRequestor] = useState(
    [],
  );
  const [selectedLegalTechUserDetails, setSelectedLegalTechUserDetails] =
    useState([]);

  const [selectedComplianceRequestors, setSelectedComplianceRequestors] =
    useState([]);
  const [selectedComplianceReviewers, setSelectedComplianceReviewers] =
    useState([]);
  const [selectedComplianceSupplier, setSelectedComplianceSupplier] = useState(
    [],
  );
  const [description, setDescription] = useState("");
  const [
    selectedComponentSupplierDetails,
    setSelectedComponentSupplierDetails,
  ] = useState([]);
  const [selectedDynamicForm, setSelectedDynamicForm] = useState(null);

  const [openAISuggestion, setOpenAISuggestion] = useState(false);
  const [showSmartMatchDialog, setshowSmartMatchDialog] = useState(false);
  const [componentToBeMatched, setComponentToBeMatched] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [
    selectedSmartMatchTestCaseSuggestions,
    setSelectedSmartMatchTestCaseSuggestions,
  ] = useState([]);
  const [allUsersList, setAllUsersList] = useState([]);
  const [disableSmartMatch, setDisableSmartMatch] = useState(false);
  const [closeAiDialogOnAdd, setCloseAiDialogOnAdd] = useState(false);
  const [openCampaignLinkDialog, setOpenCampaignLinkDialog] = useState(false);
  const [testCasesToCampaignLink, setTestCasesToCampaignLink] = useState([]);
  const [openComponentLinkDialog, setOpenComponentLinkDialog] = useState(false);
  const [testCasesToComponentLink, setTestCasesToComponentLink] = useState([]);
  const [openSupplierDialog, setOpenSupplierDialog] = useState(false);
  const [pendingSWMComponent, setPendingSWMComponent] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState("");

  const savedLinkagesRef = React.useRef(savedLinkages);
  useEffect(() => {
    savedLinkagesRef.current = savedLinkages;
  }, [savedLinkages]);

  const addFullyLinkedTestCases = (testCases = []) => {
    if (!Array.isArray(testCases) || testCases.length === 0) return;

    const idReplacementMap = new Map();
    for (const testCase of testCases) {
      const oldId = testCase?.original_test_case_id || testCase?.test_case_id;
      const newId = testCase?.new_test_case_id || testCase?.test_case_id;
      if (oldId && newId && String(oldId) !== String(newId)) {
        idReplacementMap.set(String(oldId), String(newId));
      }
    }

    if (idReplacementMap.size > 0) {
      const updatedLinkages = (savedLinkagesRef.current || []).map((linkage) =>
        updateLinkageWithNewIds(linkage, idReplacementMap),
      );

      setSavedLinkages(updatedLinkages);
    }

    setSelectedTestCaseMain((prev) => {
      const currentSelection = Array.isArray(prev) ? prev : [];
      const mergedSelection = [...currentSelection];

      for (const testCase of testCases) {
        const resolvedId = testCase?.new_test_case_id ?? testCase?.test_case_id;
        const alreadySelected = mergedSelection.some(
          (selectedItem) =>
            String(selectedItem?.test_case_id) === String(resolvedId) ||
            (testCase?.original_test_case_id && String(selectedItem?.test_case_id) === String(testCase.original_test_case_id))
        );
        
        if (alreadySelected) {
          const index = mergedSelection.findIndex(
            (item) => String(item?.test_case_id) === String(testCase?.original_test_case_id || testCase?.test_case_id)
          );
          if (index !== -1) {
            mergedSelection[index] = {
              ...mergedSelection[index],
              ...testCase,
              original_test_case_id: testCase?.original_test_case_id || testCase?.test_case_id,
              test_case_id: resolvedId,
              is_campaign_linked: true,
              is_component_linked: true,
              is_lr_linked: true,
            };
          }
        } else {
          mergedSelection.push({
            ...testCase,
            original_test_case_id: testCase?.original_test_case_id || testCase?.test_case_id,
            test_case_id: resolvedId,
            is_campaign_linked: true,
            is_component_linked: true,
            is_lr_linked: true,
          });
        }
      }

      setSelectedTestCaseForDisplay(mergedSelection);
      setSelectedSmartMatchTestCaseSuggestions(mergedSelection);
      return mergedSelection;
    });
  };

  const pipelineQueueRef = React.useRef([]);

  const runPipelineStep3 = (testCases = []) => {
    if (!Array.isArray(testCases) || testCases.length === 0) return;
    addFullyLinkedTestCases(
      testCases.map((tc) => ({
        ...tc,
        is_campaign_linked: true,
        is_component_linked: true,
        is_lr_linked: true,
      })),
    );
  };

  const runPipelineStep2 = (testCases = []) => {
    if (!Array.isArray(testCases) || testCases.length === 0) return;

    const updatedTestCases = testCases.map(tc => {
      if (tc?.new_test_case_id && String(tc.new_test_case_id) !== String(tc.test_case_id)) {
        return {
          ...tc,
          original_test_case_id: tc.test_case_id,
          test_case_id: tc.new_test_case_id
        };
      }
      return tc;
    });

    const campaignUnlinked = updatedTestCases.filter(
      (tc) => tc?.is_campaign_linked === false,
    );
    const campaignLinked = updatedTestCases.filter(
      (tc) => tc?.is_campaign_linked !== false,
    );

    if (campaignLinked.length > 0) {
      runPipelineStep3(campaignLinked);
    }

    if (campaignUnlinked.length > 0) {
      setTestCasesToCampaignLink(campaignUnlinked);
      setOpenCampaignLinkDialog(true);
    }
  };

  const handleSelectedTestCases = (testCases = []) => {
    const currentSelectedIds = new Set(
      (selectedTestCaseMain || []).map((tc) => tc?.test_case_id),
    );

    const freshTestCases = testCases.filter(
      (tc) => !currentSelectedIds.has(tc?.test_case_id),
    );

    if (freshTestCases.length === 0) return;

    const componentUnlinked = freshTestCases.filter(
      (tc) =>
        tc?.is_component_linked === false || tc?.is_lr_linked === false,
    );
    const componentLinked = freshTestCases.filter(
      (tc) =>
        tc?.is_component_linked !== false && tc?.is_lr_linked !== false,
    );

    pipelineQueueRef.current = componentLinked;

    if (componentUnlinked.length > 0) {
      setTestCasesToComponentLink(componentUnlinked);
      setOpenComponentLinkDialog(true);
    } else {
      runPipelineStep2(componentLinked);
    }
  };

  const clearCampaignLinkFields = () => {
    setOpenCampaignLinkDialog(false);
    setTestCasesToCampaignLink([]);
  };

  const openCampaignDialogForTestCases = (testCases = []) => {
    setTestCasesToCampaignLink(testCases);
    setOpenCampaignLinkDialog(true);
  };

  const handleSmartMatchRequest = async (componentDetails, tag) => {
    setDisableSmartMatch(true);
    setCloseAiDialogOnAdd(false);

    const rawItems = Array.isArray(componentDetails)
      ? componentDetails
      : componentDetails
      ? [componentDetails]
      : [];

    const fallbackItems =
      rawItems.length > 0
        ? rawItems
        : tag === ENTITY.TYPE.LEGAL
        ? selectedSWMMain || []
        : selectedLegalMain || [];

    const descriptions = fallbackItems
      .map((item) => item?.description || item?.name)
      .filter(Boolean);

    const componentName =
      descriptions.length > 0
        ? descriptions.join(", ")
        : (selectedSWMMain || [])
            .map((swm) => swm?.description || swm?.name)
            .filter(Boolean)
            .join(", ");

    if (componentName?.length > 0) {
      setComponentToBeMatched([componentName]);
    } else {
      setComponentToBeMatched(["smart-match"]);
    }

    const sourceType =
      tag === ENTITY.TYPE.LEGAL ? ENTITY.TYPE.SWM : ENTITY.TYPE.LEGAL;

    const source = fallbackItems
      .map((item) => {
        const id =
          tag === ENTITY.TYPE.LEGAL
            ? item?.swm_id || item?.id
            : item?.lr_id || item?.id;
        return {
          entityType: sourceType,
          entityId: String(id),
        };
      })
      .filter(
        (s) =>
          s.entityId &&
          s.entityId !== "undefined" &&
          s.entityId !== "null",
      );

    const shouldUseSWMScope = tag === ENTITY.TYPE.TEST_CASE;

    setshowSmartMatchDialog(true);
    setOpenAISuggestion(true);
    setSelectedEntity({
      source,
      targetEntityTypes: [tag],
      topK: 20,
      minConfidence: 0.1,
      description: componentName,
      scope: shouldUseSWMScope
        ? {
          projectId: Number(selectedProjectDetails?.project_id),
          variantId: Number(selectedVariantDetails?.variant_id),
          scopeEntities: (selectedSWMMain || []).map((swm) => ({
            entityType: ENTITY.TYPE.SWM,
            entityId: Number(swm?.swm_id || swm?.id),
          })),
        }
        : {
          projectId: Number(selectedProjectDetails?.project_id),
          variantId: Number(selectedVariantDetails?.variant_id),
        },
    });
    setDisableSmartMatch(false);
  };

  const getSuppliersList = (component) => {
    if (!component) return [];
    const suppliers = component.supplier_name || component.suppliers;
    if (Array.isArray(suppliers) && suppliers.length > 0) {
      return suppliers.map((name) => {
        const val =
          typeof name === "object"
            ? name?.name || name?.supplier_name || name?.value
            : name;
        return { label: String(val), value: String(val) };
      });
    }
    if (typeof suppliers === "string" && suppliers.trim().length > 0) {
      return [{ label: suppliers, value: suppliers }];
    }
    const fallbackSuppliers = (allUsersList || [])
      .filter(
        (u) => u?.role_details?.capability_code === CAPABILITY_CODE.supplier,
      )
      .map((u) => ({
        label: u.name || u.fullname || u.email || "Supplier",
        value: u.name || u.fullname || String(u.user_id),
      }));
    return fallbackSuppliers;
  };

  const handleAddPendingSWMComponent = () => {
    if (pendingSWMComponent) {
      const supplierValue = selectedSupplier || "";
      const componentWithSupplier = {
        ...pendingSWMComponent,
        selected_supplier: supplierValue,
      };
      setSelectedSWMMain((prev) => {
        const safePrev = Array.isArray(prev) ? prev : [];
        const exists = safePrev.some(
          (item) => item?.swm_id === componentWithSupplier.swm_id,
        );
        return exists
          ? safePrev.map((item) =>
              item?.swm_id === componentWithSupplier.swm_id
                ? {
                    ...item,
                    ...componentWithSupplier,
                    selected_supplier: supplierValue,
                  }
                : item,
            )
          : [...safePrev, componentWithSupplier];
      });
      void getSWMLinkedLRList({
        eeId: componentWithSupplier?.ee_id,
        swmId: componentWithSupplier?.swm_id,
      });
      if (selectedLegalMain?.[0]?.lr_id && componentWithSupplier?.ee_id) {
        void getDisabledSWMListForLR({
          lrId: selectedLegalMain[0].lr_id,
          eeId: componentWithSupplier.ee_id,
        });
      }
    }
    resetSupplierDialog();
  };

  const resetSupplierDialog = () => {
    setOpenSupplierDialog(false);
    setPendingSWMComponent(null);
    setSelectedSupplier("");
  };

  const handleLinkSuggestions = async (recommendation) => {
    if (recommendation.type === "SWM") {
      const swmId = recommendation?.swm_id || recommendation?.id;
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.get_sw_modules,
            params: {
              project_id: Number(selectedProjectDetails?.project_id),
              variant_id: Number(selectedVariantDetails?.variant_id),
              swm_id: Number(swmId),
              id: Number(swmId),
              page_number: 1,
              page_size: 1,
            },
            show_toast: false,
            ignore_app_loading_spinner: true,
          }),
        ),
      );
      const swmData = !resp.error && resp.data?.length ? resp.data[0] : null;
      const fullSWM = {
        ...swmData,
        ...recommendation,
        ...(swmData || {}),
        swm_id: swmId,
        id: swmId,
        supplier_name:
          swmData?.supplier_name ||
          swmData?.suppliers ||
          recommendation?.supplier_name ||
          recommendation?.suppliers ||
          [],
      };
      setPendingSWMComponent(fullSWM);
      const options = getSuppliersList(fullSWM);
      setSelectedSupplier(options.length === 1 ? options[0].value : "");
      setOpenSupplierDialog(true);
    } else if (recommendation.type === "LEGAL_REQUIREMENT") {
      handleLegalAddClick(recommendation);
    } else if (recommendation.type === "TEST_CASE") {
      handleTestCaseAddClick(recommendation);
    }
  };

  const handleLegalAddClick = (recommendation) => {
    void fetchLegalRecommendationContext({ lr_id: recommendation.lr_id });
  };

  const fetchLegalRecommendationContext = async (params) => {
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          https_method: "GET",
          ...legalRequirements.get_legal_requirements_details_new,
          params: params,
          show_toast: false,
          ignore_app_loading_spinner: true,
        }),
      ),
    );
    if (!resp.error && resp.data && resp.data.length > 0) {
      const legalRequirementData = resp.data[0];
      setSelectedLegalMain((prev) => {
        const current = prev || [];
        const exists = current.some(
          (item) => item?.lr_id === legalRequirementData?.lr_id,
        );
        const next = exists ? current : [...current, legalRequirementData];
        const allTechUsers = next.flatMap((lr) =>
          (lr?.tech_service_users || []).map((u) => ({
            ...u,
            chapter: lr?.chapter,
          })),
        );
        setSelectedLegalTechUserDetails(removeDuplicates(allTechUsers));
        return next;
      });
      setSelectedLegalForDisplay((prev) => {
        const current = prev || [];
        const exists = current.some(
          (item) => item?.lr_id === legalRequirementData?.lr_id,
        );
        return exists ? current : [...current, legalRequirementData];
      });
    }
  };

  const handleTestCaseAddClick = (recommendation) => {
    void fetchTestCaseRecommendationContext(
      {
        manual_test_case_id: recommendation.manual_test_case_id,
      },
      recommendation,
    );
  };

  const fetchTestCaseRecommendationContext = async (params, recommendation) => {
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          https_method: "GET",
          ...testCasesActions.get_test_cases,
          params: params,
          show_toast: false,
          ignore_app_loading_spinner: true,
        }),
      ),
    );
    if (!resp.error && resp.data && resp.data.length > 0) {
      const testCaseData = resp.data[0];
      const mergedTestCase = {
        ...testCaseData,
        ...recommendation,
        ...testCaseData,
        is_component_linked:
          recommendation?.is_component_linked !== undefined
            ? recommendation.is_component_linked
            : testCaseData?.is_component_linked,
        is_lr_linked:
          recommendation?.is_lr_linked !== undefined
            ? recommendation.is_lr_linked
            : testCaseData?.is_lr_linked,
        is_campaign_linked:
          recommendation?.is_campaign_linked !== undefined
            ? recommendation.is_campaign_linked
            : testCaseData?.is_campaign_linked,
      };
      handleSelectedTestCases([mergedTestCase]);
    } else if (recommendation || params) {
      handleSelectedTestCases([recommendation || params]);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      let resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...userManagementApis.get_users_list_pa,
            show_toast: false,
            params: { page_number: 1, page_size: 500 },
          }),
        ),
      );
      setAllUsersList(resp?.data || []);
    };
    void fetchUsers();
  }, [dispatch]);

  useEffect(() => {
    if (selectedSmartMatchTestCaseSuggestions.length > 0) {
      setSelectedTestCaseMain(selectedSmartMatchTestCaseSuggestions);
      setSelectedTestCaseForDisplay(selectedSmartMatchTestCaseSuggestions);
    }
  }, [selectedSmartMatchTestCaseSuggestions]);

  const getSWMLinkedLRList = async ({ eeId, swmId }) => {
    if (eeId) {
      let apiDetails = softwareIntersectionAPI.get_software_legal_requirements;

      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...apiDetails,
            params: {
              project_id: Number(selectedProjectDetails.project_id),
              variant_id: Number(selectedVariantDetails?.variant_id),
              ee_id: Number(eeId),
              ...(swmId && { swm_id: Number(swmId) }),
            },
            show_toast: false,
          }),
        ),
      );

      if (resp.error) {
        setDisabledLegalRequirement([]);
      } else {
        let linkedLrIds = [];
        if (resp.data[0]?.linked_lr_details?.[0]?.lr_id) {
          linkedLrIds = resp?.data?.[0]?.linked_lr_details?.map((lr) =>
            Number(lr?.lr_id),
          );
        }

        setDisabledLegalRequirement(linkedLrIds);
      }
    }
  };

  const getDisabledSWMListForLR = async ({ lrId, eeId }) => {
    if (!lrId || !eeId) {
      setDisabledSWMComponents([]);
      return;
    }

    if (
      !selectedProjectDetails?.project_id ||
      !selectedVariantDetails?.variant_id
    ) {
      setDisabledSWMComponents([]);
      return;
    }

    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...softwareIntersectionAPI.get_swm_for_legal_requirements,
          params: {
            project_id: Number(selectedProjectDetails?.project_id),
            variant_id: Number(selectedVariantDetails?.variant_id),
            ee_id: Number(eeId),
            lr_id: Number(lrId),
          },
          show_toast: false,
          ignore_app_loading_spinner: true,
        }),
      ),
    );

    if (resp.error) {
      setDisabledSWMComponents([]);
      return;
    }

    const swmIds = resp?.data?.[0]?.swm_ids || [];
    const normalizedIds = swmIds
      .map(Number)
      .filter((id) => Number.isFinite(id));
    setDisabledSWMComponents(normalizedIds);
  };
  const [dynamicFormFilteredData, setDynamicFormFilteredData] = useState(null);
  const [showDynamicSelect, setShowDynamicSelect] = useState(false);
  useEffect(() => {
    setZoneList(selectedProjectDetails?.project_zones);
    setIsZonalProject(selectedProjectDetails?.is_zonal);
  }, [selectedProjectDetails]);

  const clearForm = () => {
    setFormResetKey((k) => k + 1);
    setSelectedSWMMain(null);
    setSelectedLegalMain(null);
    setSelectedLegalForDisplay([]);
    setSelectedDynamicForm(null);
    setSelectedComplianceRequestors([]);
    setSelectedComplianceReviewers([]);
    setDescription("");
    setSelectedTestCaseForDisplay([]);
    setSelectedTestCaseMain([]);
    setSelectedComponentSupplierDetails([]);
    setSelectedComplianceSupplier([]);
    setDisabledSWMComponents([]);
    clearCampaignLinkFields();
  };

  useEffect(() => {
    clearForm();
    setSavedLinkages([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectDetails, selectedVariantDetails]);

  const resetTestCaseState = () => {
    setSelectedTestCaseForDisplay([]);
    setSelectedTestCaseMain([]);
    setSelectedSmartMatchTestCaseSuggestions([]);
  };

  useEffect(() => {
    resetTestCaseState();
  }, [selectedSWMMain, selectedLegalMain]);

  useEffect(() => {
    if (!selectedLegalMain?.lr_id) {
      setDisabledSWMComponents([]);
    }
  }, [selectedLegalMain]);

  useEffect(() => {
    if (selectedSWMMain && selectedSWMMain.length > 0) {
      const { requestors, suppliers } = extractUsersFromSWMData(
        selectedSWMMain,
        allUsersList,
      );
      setSelectedComponentRequestor(requestors);
      setSelectedComponentSupplierDetails(suppliers);
    } else {
      setSelectedComponentRequestor([]);
      setSelectedComponentSupplierDetails([]);
    }
  }, [selectedSWMMain, allUsersList]);

  const handleLocalSaveForm = () => {
    const selectedIds = (selectedSWMMain || []).map((swm) => swm.swm_id);
    const selectedLrIds = (selectedLegalMain || []).map((lr) => lr.lr_id);

    const existingSWM = savedLinkages.find((item) =>
      hasSameSWMs(item.swm_ids, selectedIds),
    );

    if (existingSWM) {
      const duplicateLR = existingSWM.legal.some((legal) => {
        const existingLrIds =
          legal.lr_ids || (legal.lr_id ? [legal.lr_id] : []);
        return selectedLrIds.some((id) => existingLrIds.includes(id));
      });
      if (duplicateLR) {
        alert("Unable to create duplicate ticket");
        return;
      }
      existingSWM.legal.push({
        lr_ids: selectedLrIds,
        description: description,
        oem_users: selectedComplianceRequestors.map((user) => user.value),
        tech_service_users: selectedComplianceReviewers.map(
          (user) => user.value,
        ),
        suppliers: selectedComplianceSupplier.map((user) => user.value),

        test_case_ids:
          selectedTestCaseMain?.map((tc) => tc.test_case_id) || [],
        test_cases: selectedTestCaseMain || [],
        ...(showDynamicSelect
          ? { dynamic_form: dynamicFormFilteredData }
          : {}),
      });
      setSavedLinkages([...savedLinkages]);
    } else {
      setSavedLinkages([
        ...savedLinkages,
        {
          swm_ids: selectedIds,
          addOn: selectedSWMMain,
          ee_id: selectedSWMMain?.[0]?.ee_id,
          legal: [
            {
              lr_ids: selectedLrIds,
              description: description,
              oem_users: selectedComplianceRequestors.map(
                (user) => user.value,
              ),
              tech_service_users: selectedComplianceReviewers.map(
                (user) => user.value,
              ),
              suppliers: selectedComplianceSupplier.map(
                (user) => user.value,
              ),

              test_case_ids:
                selectedTestCaseMain?.map((tc) => tc.test_case_id) || [],
              test_cases: selectedTestCaseMain || [],
              ...(showDynamicSelect
                ? { dynamic_form: dynamicFormFilteredData }
                : {}),
            },
          ],
        },
      ]);
    }
    clearForm();
  };

  const handleLegalRequirementCheckboxChange = (checked, item) => {
    const currentSelection = selectedLegalMain || [];
    let newSelectedLegal;
    if (checked) {
      const alreadyExists = currentSelection.some(
        (selectedItem) => selectedItem?.lr_id === item?.lr_id,
      );
      newSelectedLegal = alreadyExists
        ? currentSelection
        : [...currentSelection, item];
    } else {
      newSelectedLegal = currentSelection.filter(
        (selectedItem) => selectedItem?.lr_id !== item?.lr_id,
      );
    }
    setSelectedLegalMain(newSelectedLegal);
    const allTechUsers = (newSelectedLegal || []).flatMap((lr) =>
      (lr?.tech_service_users || []).map((u) => ({
        ...u,
        chapter: lr?.chapter,
      })),
    );
    setSelectedLegalTechUserDetails(removeDuplicates(allTechUsers));
  };

  const handleUnlinkSuggestions = (item) => {
    const type = item?.type || item?.raw?.targetContext?.entityType;
    const entityId = item?.swm_id ?? item?.lr_id ?? item?.test_case_id ?? item?.tc_id ?? item?.id;

    if (type === "SWM" || item?.swm_id) {
      const targetSwmId = item?.swm_id || entityId;
      setSelectedSWMMain((prev) =>
        (prev || []).filter(
          (c) => String(c?.swm_id) !== String(targetSwmId) && String(c?.id) !== String(targetSwmId),
        ),
      );
    }
    if (type === "LEGAL_REQUIREMENT" || item?.lr_id) {
      const lrId = item?.lr_id || entityId;
      setSelectedLegalMain((prev) => {
        const next = (prev || []).filter(
          (c) => String(c?.lr_id) !== String(lrId),
        );
        const allTechUsers = next.flatMap((lr) =>
          (lr?.tech_service_users || []).map((u) => ({
            ...u,
            chapter: lr?.chapter,
          })),
        );
        setSelectedLegalTechUserDetails(removeDuplicates(allTechUsers));
        return next;
      });
      setSelectedLegalForDisplay((prev) =>
        (prev || []).filter((c) => String(c?.lr_id) !== String(lrId)),
      );
    }
    if (type === "TEST_CASE" || item?.test_case_id || item?.tc_id) {
      const tcId = item?.test_case_id || item?.tc_id || entityId;
      const tcNumber = item?.manual_test_case_id || item?.req_number;
      setSelectedTestCaseMain((prev) =>
        (prev || []).filter(
          (c) =>
            String(c?.test_case_id) !== String(tcId) &&
            (!c?.original_test_case_id || String(c?.original_test_case_id) !== String(tcId)) &&
            (!tcNumber || String(c?.manual_test_case_id) !== String(tcNumber)),
        ),
      );
      setSelectedTestCaseForDisplay((prev) =>
        (prev || []).filter(
          (c) =>
            String(c?.test_case_id) !== String(tcId) &&
            (!c?.original_test_case_id || String(c?.original_test_case_id) !== String(tcId)) &&
            (!tcNumber || String(c?.manual_test_case_id) !== String(tcNumber)),
        ),
      );
      setSelectedSmartMatchTestCaseSuggestions((prev) =>
        (prev || []).filter(
          (c) =>
            String(c?.test_case_id) !== String(tcId) &&
            (!c?.original_test_case_id || String(c?.original_test_case_id) !== String(tcId)) &&
            (!tcNumber || String(c?.manual_test_case_id) !== String(tcNumber)),
        ),
      );
    }
  };

  return (
    <Box>
      <Grid container>
        <Grid item sm={12} sx={{ borderBottom: "1px solid black" }}>
          <Box sx={{ display: "flex" }}>
            <Typography sx={{ ...componentStyle.font.cardHeadder }}>
              Select Entities and Fill in the Details
            </Typography>
          </Box>
        </Grid>

        <Grid item sm={12} sx={{ pt: 1 }}>
          <RaiseTicketTemplate
            leftContent={
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  pr: 0.5,
                }}
              >
                 <Box>
                  <SelectTemplate
                    heading={"Select Software Module"}
                    isRequired={true}
                    onClickAdd={() => {
                      setOpenEESelect(true);
                    }}
                    enableEdit={selectedSWMMain && selectedSWMMain.length > 0}
                    disableSmartMatch={disableSmartMatch}
                    showSmartMatch={selectedLegalMain && selectedLegalMain.length > 0}
                    onSmartMatchClick={() =>
                      handleSmartMatchRequest(
                        selectedLegalMain,
                        ENTITY.TYPE.SWM,
                      )
                    }
                    content={
                      selectedSWMMain && selectedSWMMain.length > 0 ? (
                        <Box sx={{ pl: 2, display: "flex", flexDirection: "column", gap: 1.5, maxHeight: "200px", overflowY: "auto" }}>
                          {selectedSWMMain.map((swm, idx) => (
                            <KeyValueDisplayCard
                              key={swm.swm_id || idx}
                              data={[
                                {
                                  key: "Name",
                                  value: swm.name,
                                  style: { fontWeight: 600 },
                                },
                                {
                                  key: "Description",
                                  value: swm.description,
                                },
                                {
                                  key: "Version",
                                  value: swm.version,
                                },
                              ]}
                            />
                          ))}
                        </Box>
                      ) : (
                        <></>
                      )
                    }
                  />
                </Box>

                <Box>
                  <SelectTemplate
                    heading={"Select Legal Requirement"}
                    isRequired={true}
                    onClickAdd={() => {
                      setOpenLegalSelect(true);
                    }}
                    enableEdit={selectedLegalForDisplay && selectedLegalForDisplay.length > 0}
                    disableSmartMatch={disableSmartMatch}
                    showSmartMatch={selectedSWMMain && selectedSWMMain.length > 0}
                    onSmartMatchClick={() =>
                      handleSmartMatchRequest(
                        selectedSWMMain,
                        ENTITY.TYPE.LEGAL,
                      )
                    }
                    content={
                      selectedLegalForDisplay && selectedLegalForDisplay.length > 0 ? (
                        <Box sx={{ pl: 2, maxHeight: "200px", overflowY: "auto" }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              justifyContent: "left",
                              pl: 1,
                              width: "100%",
                            }}
                          >
                            <Typography
                              sx={{
                                ...componentStyle.font.text,
                                fontWeight: 600,
                              }}
                            >
                              Legal Standard:
                            </Typography>
                            <Typography
                              sx={{
                                ...componentStyle.font.text,
                                textAlign: "left",
                                width: "100%",
                              }}
                            >
                              {`Name: ${selectedLegalForDisplay[0]?.legal_standard_details?.name || ""}`}
                            </Typography>
                            <Typography
                              sx={{
                                ...componentStyle.font.text,
                                textAlign: "left",
                                width: "100%",
                              }}
                            >
                              {`Number: ${selectedLegalForDisplay[0]?.legal_standard_details?.number || ""}`}
                            </Typography>
                            <Typography
                              sx={{
                                ...componentStyle.font.text,
                                textAlign: "left",
                                width: "100%",
                              }}
                            >
                              {`Revision: ${selectedLegalForDisplay[0]?.legal_standard_details?.revision || ""}`}
                            </Typography>

                            <Typography
                              sx={{
                                ...componentStyle.font.text,
                                fontWeight: 600,
                                mt: 1,
                              }}
                            >
                              Legal Requirements:
                            </Typography>
                            {selectedLegalForDisplay.map((lr, idx) => (
                              <Box
                                key={lr.lr_id || idx}
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "start",
                                  position: "relative",
                                  mb: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: "10%",
                                  }}
                                >
                                  <CustomCheckBoxWithoutFormControl
                                    value={
                                      selectedLegalMain?.some(
                                        (selectedItem) =>
                                          selectedItem?.lr_id === lr?.lr_id,
                                      ) || false
                                    }
                                    onChange={(e) =>
                                      handleLegalRequirementCheckboxChange(
                                        e.target.checked,
                                        lr,
                                      )
                                    }
                                  />
                                </Box>
                                <Box
                                  sx={{
                                    width: "90%",
                                    pl: 1,
                                    borderLeft: "2px solid #ccc",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      ...componentStyle.font.text,
                                      textAlign: "left",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {`Number: ${lr.req_number || ""}`}
                                  </Typography>
                                  <CustomToolTip
                                    length={200}
                                    style={{
                                      ...componentStyle.font.text,
                                      textAlign: "left",
                                      width: "100%",
                                    }}
                                  >
                                    {`Description: ${lr.description || ""}`}
                                  </CustomToolTip>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      ) : (
                        <></>
                      )
                    }
                  />
                </Box>

                <Box>
                  <SelectTemplate
                    heading={"Select Test Case"}
                    onClickAdd={() => {
                      setOpenTestCaseSelect(true);
                    }}
                    disableSmartMatch={disableSmartMatch}
                    addButtonDisabled={
                      !selectedLegalMain || selectedLegalMain.length === 0 || !selectedSWMMain || selectedSWMMain.length === 0
                    }
                    enableEdit={
                      selectedTestCaseForDisplay !== null &&
                      selectedTestCaseForDisplay.length > 0
                    }
                    showSmartMatch={
                      selectedSWMMain && selectedSWMMain.length > 0 && selectedLegalMain && selectedLegalMain.length > 0
                    }
                    onSmartMatchClick={() =>
                      handleSmartMatchRequest(
                        selectedLegalMain,
                        ENTITY.TYPE.TEST_CASE,
                      )
                    }
                    content={
                      <Box sx={{ pl: 2 }}>
                        {selectedTestCaseForDisplay?.length > 0 &&
                          selectedTestCaseForDisplay.map((item) => (
                            <Box
                              key={item.test_case_id}
                              sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "start",
                                position: "relative",
                              }}
                            >
                              <Box
                                sx={{
                                  width: "10%",
                                }}
                              >
                                <CustomCheckBoxWithoutFormControl
                                  value={
                                    selectedTestCaseMain?.some(
                                      (selectedItem) =>
                                        selectedItem?.test_case_id ===
                                        item?.test_case_id,
                                    ) || false
                                  }
                                  onChange={(e) => {
                                    const currentSelection =
                                      selectedTestCaseMain || [];
                                    if (
                                      e.target.checked &&
                                      item?.is_campaign_linked === false
                                    ) {
                                      openCampaignDialogForTestCases([item]);
                                      return;
                                    }
                                    let newSelectedTestCase;
                                    if (e.target.checked) {
                                      const alreadyExists = currentSelection.some(
                                        (selectedItem) => selectedItem?.test_case_id === item?.test_case_id
                                      );
                                      newSelectedTestCase = alreadyExists ? currentSelection : [...currentSelection, item];
                                    } else {
                                      newSelectedTestCase = currentSelection.filter(
                                        (selectedItem) => selectedItem?.test_case_id !== item?.test_case_id
                                      );
                                    }
                                    setSelectedTestCaseMain(
                                      newSelectedTestCase,
                                    );
                                    setSelectedTestCaseForDisplay(
                                      newSelectedTestCase,
                                    );
                                  }}
                                />
                              </Box>
                              <Box
                                sx={{
                                  width: "60%",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    p: 1,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      ...componentStyle.font.text,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "100%",
                                      display: "flex",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                        fontWeight: 600,
                                      }}
                                    >{`Test Case ID: `}</Box>
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                      }}
                                    >
                                      {item.test_case_id}
                                    </Box>
                                  </Typography>
                                  <Typography
                                    sx={{
                                      ...componentStyle.font.text,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "100%",
                                      display: "flex",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                        fontWeight: 600,
                                      }}
                                    >{`Test Case Title: `}</Box>
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                      }}
                                    >
                                      {item.name}
                                    </Box>
                                  </Typography>
                                  <Typography
                                    sx={{
                                      ...componentStyle.font.text,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "100%",
                                      display: "flex",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                        fontWeight: 600,
                                      }}
                                    >{`Description: `}</Box>
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                      }}
                                    >
                                      {item.description}
                                    </Box>
                                  </Typography>
                                </Box>
                              </Box>
                              <Box
                                sx={{
                                  width: "30%",
                                }}
                              >
                              </Box>
                            </Box>
                          ))}
                      </Box>
                    }
                  />
                </Box>
              </Box>
            }
            rightContent={
              <RaiseTicketRightTemplate
                selectedComponentRequestor={selectedComponentRequestor}
                selectedLegalTechUserDetails={selectedLegalTechUserDetails}
                selectedComplianceRequestors={selectedComplianceRequestors}
                setSelectedComplianceRequestor={setSelectedComplianceRequestors}
                selectedComplianceReviewers={selectedComplianceReviewers}
                setSelectedComplianceReviewers={setSelectedComplianceReviewers}
                selectedComponent={selectedSWMMain}
                description={description}
                formType={entityType}
                setDescription={setDescription}
                selectedComponentSupplierDetails={
                  selectedComponentSupplierDetails
                }
                selectedComplianceSupplier={selectedComplianceSupplier}
                setSelectedComplianceSupplier={setSelectedComplianceSupplier}
                selectedDynamicForm={selectedDynamicForm}
                setSelectedDynamicForm={setSelectedDynamicForm}
                dynamicFormFilteredData={dynamicFormFilteredData}
                setDynamicFormFilteredData={setDynamicFormFilteredData}
                showDynamicSelect={showDynamicSelect}
                setShowDynamicSelect={setShowDynamicSelect}
                localSubmitDisabled={
                  !selectedSWMMain ||
                  selectedSWMMain.length === 0 ||
                  !selectedLegalMain ||
                  selectedLegalMain.length === 0 ||
                  description === "" ||
                  description.trim().length < 3 ||
                  selectedComplianceRequestors.length === 0 ||
                  selectedComplianceReviewers.length === 0
                }
                handleLocalSaveForm={handleLocalSaveForm}
                savedLinkageCard={
                  <EESWMSavedLinkageCard
                    savedLinkages={savedLinkages}
                    setSavedLinkages={setSavedLinkages}
                    entityType={"swm"}
                    onRemove={clearForm}
                  />
                }
              />
            }
          />
        </Grid>
      </Grid>

      <DialogIndex
        key={formResetKey}
        openEESelect={openEESelect}
        setOpenEESelect={setOpenEESelect}
        isZonalProject={isZonalProject}
        zoneList={zoneList}
        savedLinkages={savedLinkages}
        disabledSWMComponents={disabledSWMComponents}
        onSelectEEForSWM={(eeData) => {
          if (selectedLegalMain?.[0]?.lr_id && eeData?.ee_id) {
            void getDisabledSWMListForLR({
              lrId: selectedLegalMain[0].lr_id,
              eeId: eeData.ee_id,
            });
          } else {
            setDisabledSWMComponents([]);
          }
        }}
        disableLrRadioButton={(item) => {
          const eeExists = savedLinkages?.find(
            (linkage) => linkage.ee_id === selectedSWMMain?.[0]?.ee_id,
          );
          return eeExists
            ? eeExists.legal?.some((legal) => legal.lr_id === item.lr_id)
            : false;
        }}
        selectedSWMMain={selectedSWMMain}
        onClickAdd={async (data) => {
          if (selectedLegalMain && selectedLegalMain.length > 0) {
            const entity = (data || []).map((swm) => ({
              id: Number(swm.swm_id),
              supplier_name: swm.selected_supplier || (Array.isArray(swm.supplier_name) ? swm.supplier_name[0] : swm.supplier_name) || ""
            }));
            const lrIds = selectedLegalMain.map((x) => Number(x.lr_id));
            const duplicateCheck = await Promise.resolve(
              dispatch(
                HandleApiActions({
                  ...typeApprovalAPI.check_for_duplicate,
                  params: {
                    ticket_type: 1,
                    entity: entity,
                    lr_ids: lrIds,
                  },
                  show_toast: false,
                  ignore_app_loading_spinner: true,
                }),
              ),
            );
            if (duplicateCheck && !duplicateCheck.error && duplicateCheck.data?.intr_id?.length > 0) {
              setDuplicateAlertMessage(`Ticket for selected Software component and legal requirement combination already exist in intersection id : ${duplicateCheck.data.intr_id.join(", ")}`);
              setOpenDuplicateAlert(true);
              return;
            }
          }

          setSelectedSWMMain(data);
          if (Array.isArray(data) && data.length > 0) {
            await getSWMLinkedLRList({
              eeId: data[0].ee_id,
              swmId: data[0].swm_id,
            });
          }
          setOpenEESelect(false);

          const ownerDetails = gatherFromAll(data, "owner_details", allUsersList);
          const coOwnerDetails = [
            ...gatherFromAll(data, "co_owners", allUsersList),
            ...gatherFromAll(data, "co_owner_details", allUsersList),
          ];
          const editorDetails = gatherFromAll(data, "edit_user_details", allUsersList);
          const viewersDetails = gatherFromAll(data, "view_user_details", allUsersList);

          let duplicateRemovedComponentRequestor = removeDuplicates([
            ...ownerDetails,
            ...coOwnerDetails,
          ]);
          setSelectedComponentRequestor(
            duplicateRemovedComponentRequestor.filter((ele) => {
              return ele?.capability_code !== CAPABILITY_CODE.supplier;
            }),
          );

          let duplicateRemovedComponentSupplierDetails = removeDuplicates([
            ...coOwnerDetails,
            ...editorDetails,
            ...viewersDetails,
          ]);
          setSelectedComponentSupplierDetails(
            duplicateRemovedComponentSupplierDetails.filter((ele) => {
              return ele?.capability_code === CAPABILITY_CODE.supplier;
            }),
          );
        }}
        openLegalSelect={openLegalSelect}
        setOpenLegalSelect={setOpenLegalSelect}
        selectedLegalMain={selectedLegalMain}
        setSelectedLegalMain={setSelectedLegalMain}
        disabledLegalRequirement={disabledLegalRequirement}
        onClickAddLegal={async (data) => {
          if (selectedSWMMain && selectedSWMMain.length > 0) {
            const entity = selectedSWMMain.map((swm) => ({
              id: Number(swm.swm_id),
              supplier_name: swm.selected_supplier || (Array.isArray(swm.supplier_name) ? swm.supplier_name[0] : swm.supplier_name) || ""
            }));
            const lrIds = (data || []).map((x) => Number(x.lr_id));
            const duplicateCheck = await Promise.resolve(
              dispatch(
                HandleApiActions({
                  ...typeApprovalAPI.check_for_duplicate,
                  params: {
                    ticket_type: 1,
                    entity: entity,
                    lr_ids: lrIds,
                  },
                  show_toast: false,
                  ignore_app_loading_spinner: true,
                }),
              ),
            );
            if (duplicateCheck && !duplicateCheck.error && duplicateCheck.data?.intr_id?.length > 0) {
              setDuplicateAlertMessage(`Ticket for selected Software component and legal requirement combination already exist in intersection id : ${duplicateCheck.data.intr_id.join(", ")}`);
              setOpenDuplicateAlert(true);
              return;
            }
          }

          setSelectedLegalMain(data);
          setSelectedLegalForDisplay(data);
          setOpenLegalSelect(false);
          const allTechUsers = (data || []).flatMap((lr) => (lr?.tech_service_users || []).map(u => ({...u, chapter: lr?.chapter})));
          setSelectedLegalTechUserDetails(removeDuplicates(allTechUsers));
        }}
        openTestCaseSelect={openTestCaseSelect}
        setOpenTestCaseSelect={setOpenTestCaseSelect}
        selectedTextCaseMain={selectedTestCaseMain}
        setSelectedTestCaseMain={setSelectedTestCaseMain}
        onClickAddTestCase={(data) => {
          handleSelectedTestCases(data);
          setOpenTestCaseSelect(false);
        }}
        entityID={selectedSWMMain?.[0]?.swm_id}
        entityType={"swm"}
      />
      {showSmartMatchDialog && componentToBeMatched?.length > 0 && (
        <AISuggestionDialogContainer
          open={openAISuggestion}
          onClose={() => setOpenAISuggestion(false)}
          entity={selectedEntity}
          onLinkRequest={handleLinkSuggestions}
          onUnlinkRequest={handleUnlinkSuggestions}
          closeDialogOnAdd={closeAiDialogOnAdd}
          selectedComponents={selectedSWMMain}
          selectedLegals={selectedLegalMain}
          selectedTestCases={selectedTestCaseMain}
        />
      )}
      <TestCaseCampaignLinkDialog
       open={openCampaignLinkDialog}
        projectId={selectedProjectDetails?.project_id}
        variantId={selectedVariantDetails?.variant_id}
        onClose={clearCampaignLinkFields}
        testCasesToLink={testCasesToCampaignLink}
        onSuccess={(linkedTestCases) => {
          clearCampaignLinkFields();
          runPipelineStep3(
            (linkedTestCases || []).map((tc) => ({
              ...tc,
              is_campaign_linked: true,
            })),
          );
        }}
      />
      <TestCaseComponentLinkDialog
        entityType="1"
        open={openComponentLinkDialog}
        testCasesToLink={testCasesToComponentLink}
        selectedEEMain={selectedSWMMain}
        selectedLegalMain={selectedLegalMain}
        onClose={() => setOpenComponentLinkDialog(false)}
        onSuccess={(linkedTestCases) => {
          setOpenComponentLinkDialog(false);
          const componentLinkedCases = (linkedTestCases || []).map((tc) => ({
            ...tc,
            is_component_linked: true,
            is_lr_linked: true,
          }));
          const allCasesForStep2 = [
            ...pipelineQueueRef.current,
            ...componentLinkedCases,
          ];
          pipelineQueueRef.current = [];
          runPipelineStep2(allCasesForStep2);
        }}
      />
      <CustomDialogBoxV2
        open={openDuplicateAlert}
        onClose={() => setOpenDuplicateAlert(false)}
        title="Duplicate Ticket Alert"
        hideDefaultActions={true}
        footerSlot={
          <DialogActions sx={{ pr: 3, pl: 3, pb: 2, justifyContent: "flex-end" }}>
            <Button
              onClick={() => setOpenDuplicateAlert(false)}
              variant="contained"
              sx={{
                backgroundColor: "black",
                color: "white",
                "&:hover": {
                  backgroundColor: "#333",
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        }
      >
        <Typography>{duplicateAlertMessage}</Typography>
      </CustomDialogBoxV2>
      <CustomDialogBoxV2
        title="Select Supplier"
        open={openSupplierDialog}
        onClose={resetSupplierDialog}
        sx={{ zIndex: 1400 }}
        dialogActions={
          <SelectDialogActionButtons
            enableSubmit={
              Boolean(selectedSupplier) ||
              getSuppliersList(pendingSWMComponent).length === 0
            }
            onClickAdd={handleAddPendingSWMComponent}
            onClickCancel={resetSupplierDialog}
          />
        }
      >
        <Box sx={{ p: 2, minWidth: "300px" }}>
          <CustomSelectV2
            label="Select Supplier"
            optionsList={getSuppliersList(pendingSWMComponent)}
            selectedValue={selectedSupplier}
            handleOnChange={(e) => setSelectedSupplier(e.target.value)}
          />
        </Box>
      </CustomDialogBoxV2>
    </Box>
  );
}

RaiseSoftwareTickets.propTypes = {
  savedLinkages: PropTypes.array,
  setSavedLinkages: PropTypes.func,
  entityType: PropTypes.string,
};

export default RaiseSoftwareTickets;
